import { memo } from "react";
import { Link, useLocation } from "react-router-dom";

import styled from "styled-components";

import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { useBillingStore } from "stores/BillingStore";
import { PermissionLevel, useUserStore } from "stores/UserStore";

// -----------------------------------------------------------------------------

const FreeTrialBar = memo(function FreeTrialBar(): JSX.Element | null {
  const location = useLocation();
  const [userStore, actions] = useUserStore();
  const [billingStore] = useBillingStore();

  // Get trial info and don't show the bar if they're not in a free trial
  const {
    subscription: { is_trial: isTrial = false, days_left: daysLeft = 0 } = {},
  } = billingStore;

  /* Don't show free trial bar if:
   ** 1. User is already on Billing page since there's already context and a CTA for users
   ** 2. User has dismissed the bar in this session (logging back in will resurface bar)
   ** 3. User is not in a trial
   ** 4. daysLeft is invalid
   */
  const isBillingPage =
    location.pathname === "/organization/settings/billing/plan";
  const isFreeTrialBarDismissed = userStore.isFreeTrialBarDismissed;
  const trialExpired = daysLeft < 0;
  const dontRender =
    isBillingPage || isFreeTrialBarDismissed || !isTrial || trialExpired;

  //
  const canManageBilling =
    userStore.user.permissionLevel === PermissionLevel.SuperAdmin;

  //
  return dontRender ? null : (
    <Bar>
      <Stack direction="row" spacing={1} alignItems="center">
        <InfoOutlinedIcon color="info" />
        <LabelWarning color="primary">
          You have <strong>{daysLeft} days</strong> left on your fully-featured
          free trial.
        </LabelWarning>
        {canManageBilling && (
          <Button
            component={Link}
            to="/organization/settings/billing/plan"
            variant="contained"
            color="primary"
          >
            Manage plan
          </Button>
        )}
        <IconButton
          aria-label="delete"
          onClick={() => {
            actions.dismissFreeTrialBar();
          }}
        >
          <CloseIcon />
        </IconButton>
      </Stack>
    </Bar>
  );
});

export default FreeTrialBar;

// -----------------------------------------------------------------------------

const Bar = styled.div`
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  background-color: #ffb70021;
  padding: 8px;
  box-sizing: border-box;
`;

const LabelWarning = styled(Typography)`
  flex-grow: 1;
`;
